import React, { useEffect, useMemo, useState } from 'react';
import { addCommas } from '@persian-tools/persian-tools';
import { GridColDef } from '@mui/x-data-grid';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Dialog from 'components/dialog/dialog';
import MuiDataTable from 'components/mui-data-table/mui-data-table';
import { useWalletTransactionQuery } from 'app/store';
import TransactionStatusBullet from './marketing-transaction-status-bullet';
import { noRowsTitle, timeConverter } from 'utils/utils';

const walletsTitle: Record<string, string> = {
    sms: 'کیف پول پیامک',
    wage: 'کیف پول کارمزد',
};

const baseColumns: GridColDef[] = [
    {
        field: 'id',
        headerName: '#',
        width: 72,
    },
    {
        field: 'amount',
        headerName: 'مبلغ',
        minWidth: 125,
        renderCell: ({ row }) => `${addCommas(row.amount ?? 0)} ریال`,
    },
    {
        field: 'before_amount',
        headerName: 'موجودی قبل',
        minWidth: 130,
        renderCell: ({ row }) => `${addCommas(row.before_amount ?? 0)} ریال`,
    },
    {
        field: 'after_amount',
        headerName: 'موجودی بعد',
        minWidth: 130,
        renderCell: ({ row }) => `${addCommas(row.after_amount ?? 0)} ریال`,
    },
    {
        field: 'refrence_code',
        headerName: 'کد تراکنش',
        minWidth: 145,
        cellClassName: 'ltr-cell',
        renderCell: ({ row }) => row.refrence_code || `#${row.transaction_id}`,
    },
    {
        field: 'type',
        headerName: 'نوع',
        minWidth: 105,
        renderCell: ({ row }) => <TransactionStatusBullet status={row?.type} />,
    },
    {
        field: 'created_at',
        headerName: 'زمان',
        minWidth: 175,
        renderCell: ({ row }) =>
            row.created_at ? timeConverter(row.created_at) : '—',
    },
];

const TransactionsDataTable: React.FC<{
    wallet: any;
    onClose: any;
    showDetails: boolean;
}> = ({ wallet, onClose, showDetails }) => {
    const navigate = useNavigate();
    const [rowsData, setRowsData] = useState<any[]>([]);
    const [page, setPage] = useState<number>(1);
    const [paginateInfo, setPaginateInfo] = useState(undefined);

    const columns = useMemo(() => {
        if (wallet?.name !== 'sms') {
            return baseColumns;
        }

        return baseColumns.filter(
            ({ field }) => field !== 'refrence_code' && field !== 'created_at',
        );
    }, [wallet?.name]);

    const {
        data,
        isFetching: isLoadingData,
        isSuccess,
        refetch,
    } = useWalletTransactionQuery(
        {
            id: Number(wallet?.id),
            page,
            perPage: 15,
        },
        { skip: !wallet?.id },
    );

    useEffect(() => {
        setPage(1);
    }, [wallet?.id]);

    useEffect(() => {
        if (isSuccess && !isLoadingData) {
            setRowsData(data?.items ?? []);
            setPage(data?.pagination?.current_page ?? 1);
            setPaginateInfo(data?.pagination);
        }
    }, [data, isSuccess, isLoadingData]);

    if (!wallet?.id) return null;

    return (
        <Dialog
            label={`گزارشات ${walletsTitle[wallet?.name] ?? 'کیف پول'}`}
            open={!!wallet?.id}
            onClose={onClose}
            maxWidth="md"
            width="960px"
            icon={<i className="fa-light fa-chart-line" />}
            type="warning"
        >
            <Box
                sx={{
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2.25,
                }}
            >
                <MuiDataTable
                    rows={rowsData}
                    columns={columns}
                    isLoading={isLoadingData}
                    isSuccess={isSuccess}
                    onRefetch={refetch}
                    paginateInfo={paginateInfo}
                    setPage={setPage}
                    onClickFunction={
                        showDetails
                            ? (params) => navigate('/transaction/' + params?.id)
                            : undefined
                    }
                    noRowsMessage={noRowsTitle('تراکنشی')}
                />
            </Box>
        </Dialog>
    );
};

export default TransactionsDataTable;
